/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import { X, UserPlus, Save } from "lucide-react";

/**
 * Hybrid autocomplete-multi-select for tagging individuals on records.
 * Suggestions come from the match's Responsible Persons directory.
 * Freeform names are supported; a small "+ Add to Contacts" pill sits on
 * every freeform tag so admins can promote it into the directory later.
 * `value` is an array of { id?, name, dept_designation?, contact_no? }.
 */
export default function PeopleTagger({
  value = [],
  onChange,
  placeholder = "Tag a person…",
  testidPrefix = "tagger",
}) {
  const { selectedMatchId } = useMatch();
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [promotingIdx, setPromotingIdx] = useState(null);
  const [promoteForm, setPromoteForm] = useState({ dept_designation: "", contact_no: "" });
  const [saving, setSaving] = useState(false);
  const wrapRef = useRef(null);

  const loadContacts = () => {
    if (!selectedMatchId) return;
    api.get(`/matches/${selectedMatchId}/contacts`).then((r) => setContacts(r.data)).catch(() => {});
  };
  useEffect(() => { loadContacts(); }, [selectedMatchId]);

  useEffect(() => {
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const taggedIds = new Set(value.filter((v) => v.id).map((v) => v.id));
  const taggedNames = new Set(value.map((v) => (v.name || "").toLowerCase()));

  const suggestions = contacts
    .filter((c) => !taggedIds.has(c.id))
    .filter((c) => {
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return c.name.toLowerCase().includes(s) || (c.dept_designation || "").toLowerCase().includes(s);
    })
    .slice(0, 8);

  const canAddFreeform =
    q.trim() &&
    !taggedNames.has(q.trim().toLowerCase()) &&
    !contacts.some((c) => c.name.toLowerCase() === q.trim().toLowerCase());

  const addContact = (c) => {
    onChange([...value, { id: c.id, name: c.name, dept_designation: c.dept_designation, contact_no: c.contact_no }]);
    setQ("");
  };
  const addFreeform = () => {
    if (!q.trim()) return;
    onChange([...value, { name: q.trim() }]);
    setQ("");
  };
  const quickAddContact = async () => {
    if (!q.trim() || !selectedMatchId) return;
    try {
      const { data } = await api.post(`/matches/${selectedMatchId}/contacts`, {
        dept_designation: "Uncategorised",
        name: q.trim(),
        contact_no: "",
      });
      setContacts((cs) => [...cs, data]);
      addContact(data);
    } catch { /* ignore */ }
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  const openPromote = (idx) => {
    setPromotingIdx(idx);
    setPromoteForm({ dept_designation: "", contact_no: "" });
  };
  const submitPromote = async (e) => {
    e?.preventDefault();
    if (promotingIdx == null || !selectedMatchId) return;
    const target = value[promotingIdx];
    if (!target || target.id) { setPromotingIdx(null); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/matches/${selectedMatchId}/contacts`, {
        dept_designation: (promoteForm.dept_designation || "Uncategorised").trim() || "Uncategorised",
        name: target.name,
        contact_no: promoteForm.contact_no.trim(),
      });
      setContacts((cs) => [...cs, data]);
      // upgrade the freeform pill into a full contact reference in-place
      const next = value.slice();
      next[promotingIdx] = { id: data.id, name: data.name, dept_designation: data.dept_designation, contact_no: data.contact_no };
      onChange(next);
      setPromotingIdx(null);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testidPrefix}-wrap`}>
      <div className="flex flex-wrap gap-1 items-center bg-black border border-cmdBorder p-1.5 min-h-[42px]">
        {value.map((p, i) => {
          const isFreeform = !p.id;
          return (
            <span
              key={i}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs ${
                isFreeform
                  ? "bg-warnAmber/10 border border-dashed border-warnAmber text-warnAmber"
                  : "bg-warnAmber/20 border border-warnAmber text-warnAmber"
              }`}
              data-testid={`${testidPrefix}-pill-${i}`}
              title={
                isFreeform
                  ? "Freeform tag — not yet in Responsible Persons directory"
                  : [p.dept_designation, p.contact_no].filter(Boolean).join(" · ") || ""
              }
            >
              {p.name}
              {isFreeform && (
                <button
                  type="button"
                  onClick={() => openPromote(i)}
                  className="text-readyGreen hover:text-white"
                  data-testid={`${testidPrefix}-pill-promote-${i}`}
                  title="Add to Responsible Persons directory"
                >
                  <UserPlus className="w-3 h-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(i)}
                className="hover:text-white"
                data-testid={`${testidPrefix}-pill-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
        <input
          type="text"
          className="flex-1 min-w-[110px] bg-transparent outline-none text-sm px-2"
          placeholder={value.length === 0 ? placeholder : "Add another…"}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) { e.preventDefault(); addFreeform(); } }}
          data-testid={`${testidPrefix}-input`}
        />
      </div>
      {open && (suggestions.length > 0 || canAddFreeform) && (
        <div className="absolute z-40 left-0 right-0 top-full mt-1 mc-panel max-h-64 overflow-auto" data-testid={`${testidPrefix}-dropdown`}>
          {suggestions.map((c) => (
            <button
              type="button"
              key={c.id}
              onClick={() => addContact(c)}
              className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-cmdBorder last:border-b-0 flex items-center justify-between gap-2"
              data-testid={`${testidPrefix}-suggest-${c.id}`}
            >
              <div>
                <div className="text-sm font-medium">{c.name}</div>
                <div className="text-xs text-zinc-500">{c.dept_designation}</div>
              </div>
              {c.contact_no && <span className="text-xs text-zinc-500 font-mono-live">{c.contact_no}</span>}
            </button>
          ))}
          {canAddFreeform && (
            <div className="flex items-stretch">
              <button
                type="button"
                onClick={addFreeform}
                className="flex-1 text-left px-3 py-2 hover:bg-white/5 border-t border-cmdBorder text-xs"
                data-testid={`${testidPrefix}-add-freeform`}
              >
                Tag "<span className="font-medium">{q}</span>" as freeform
              </button>
              <button
                type="button"
                onClick={quickAddContact}
                className="flex items-center gap-1 px-3 py-2 hover:bg-white/5 border-t border-l border-cmdBorder text-xs text-readyGreen"
                data-testid={`${testidPrefix}-add-contact`}
              >
                <UserPlus className="w-3 h-3" /> Add to Contacts
              </button>
            </div>
          )}
        </div>
      )}

      {/* Promote-to-contact inline mini-form */}
      {promotingIdx != null && value[promotingIdx] && !value[promotingIdx].id && (
        <form
          onSubmit={submitPromote}
          className="absolute z-40 left-0 right-0 top-full mt-1 mc-panel p-3 space-y-2"
          data-testid={`${testidPrefix}-promote-form`}
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="mc-label">Add to Responsible Persons</div>
              <div className="text-sm font-medium mt-0.5">{value[promotingIdx].name}</div>
            </div>
            <button
              type="button"
              onClick={() => setPromotingIdx(null)}
              className="text-zinc-500 hover:text-white"
              data-testid={`${testidPrefix}-promote-cancel`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Department / Designation"
              value={promoteForm.dept_designation}
              onChange={(e) => setPromoteForm({ ...promoteForm, dept_designation: e.target.value })}
              data-testid={`${testidPrefix}-promote-dept`}
              autoFocus
            />
            <input
              className="bg-black border border-cmdBorder p-2 text-sm font-mono-live"
              placeholder="Contact number"
              value={promoteForm.contact_no}
              onChange={(e) => setPromoteForm({ ...promoteForm, contact_no: e.target.value })}
              data-testid={`${testidPrefix}-promote-contact`}
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full flex items-center justify-center gap-1 bg-readyGreen text-white py-1.5 font-bold uppercase tracking-widest text-xs disabled:opacity-60"
            data-testid={`${testidPrefix}-promote-submit`}
          >
            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save to Directory"}
          </button>
        </form>
      )}
    </div>
  );
}

/** Compact read-only display for tagged people. */
export function PeoplePills({ people = [], testidPrefix = "pills" }) {
  if (!people || !people.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1 items-center align-middle" data-testid={`${testidPrefix}-wrap`}>
      {people.map((p, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 border border-warnAmber/50 text-warnAmber px-1.5 py-0.5 text-[10px] font-mono-live"
          title={[p.dept_designation, p.contact_no].filter(Boolean).join(" · ") || ""}
        >
          {p.contact_no ? <a href={`tel:${p.contact_no}`} className="hover:underline">{p.name}</a> : p.name}
        </span>
      ))}
    </span>
  );
}
